import { useState } from "react";
import styled from "styled-components";
import { Button, Modal, Input, message } from "antd";
import PostAuthor from "./PostAuthor";
import EditorSearch from "./EditorSearch";
import { FreeTextContent } from "../freetext";
import { ContentFormat } from "../../api/postAPI";

interface PostContentSectionProps {
  title: string;
  hasEditAuth: boolean;
  onAuthors: any[];
  avatarUrls: Record<string, string>;
  isSecret: boolean;
  onClickEdit: () => void;
  onClickDelete: () => void;
  onClickAuthorEdit: () => void;
  onSetPassword?: (password: string) => void;
  onRemovePassword?: () => void;
  // FreeText 본문 (블록 방식이면 #editorJS 에 Editor.js 가 그린다)
  contentFormat?: ContentFormat;
  content?: string | null;
  contentEn?: string | null;
}

const PostContentSection = ({
  contentFormat,
  content,
  contentEn,
  title,
  hasEditAuth,
  onAuthors,
  avatarUrls,
  isSecret,
  onClickEdit,
  onClickDelete,
  onClickAuthorEdit,
  onSetPassword,
  onRemovePassword,
}: PostContentSectionProps) => {
  const showAuthorsLabel = false;
  const showAuthorDescription = false;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [password, setPassword] = useState("");

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    const isValidPassword = /^\d{4}$/.test(password);
    if (!isValidPassword) {
      message.warning("비밀번호는 4자리 숫자로 입력해주세요.");
      return;
    }
    if (onSetPassword) {
      onSetPassword(password);
    }
    setIsModalOpen(false);
    setPassword("");
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setPassword("");
  };

  return (
    <ContentWrapper>
      <PostTitle>{title}</PostTitle>

      <ActionBar>
        <EditorSearch />
        {hasEditAuth && (
          <ActionButtons>
            {isSecret ? (
               <Button size="small" onClick={onRemovePassword} style={{ fontSize: "12px" }}>
                 비밀번호 해제
               </Button>
            ) : (
               <Button size="small" onClick={showModal} style={{ fontSize: "12px" }}>
                 비밀번호 설정
               </Button>
            )}
            <Button type="default" size="small" onClick={onClickEdit} style={{ fontSize: "12px" }}>
              Edit
            </Button>
            <Button type="default" size="small" danger onClick={onClickDelete} style={{ fontSize: "12px" }}>
              Delete
            </Button>
            <Button type="primary" size="small" onClick={onClickAuthorEdit} style={{ fontSize: "12px" }}>
              {!(onAuthors?.length > 0) ? "Add author" : "Edit author"}
            </Button>
          </ActionButtons>
        )}
      </ActionBar>

      {onAuthors?.length > 0 && (
        <AuthorsWrapper>
          {showAuthorsLabel && <AuthorsLabel>Authors</AuthorsLabel>}
          <AuthorsList>
            {onAuthors.map((author: any, index: number) => (
              <PostAuthor
                key={`${author?.name}_${index}_img`}
                data={author}
                avatarUrls={avatarUrls}
                showDescription={showAuthorDescription}
              />
            ))}
          </AuthorsList>
        </AuthorsWrapper>
      )}

      <EditorWrapper>
        {contentFormat === "FREETEXT" ? (
          <FreeTextContent id="editorJS" html={content} htmlEn={contentEn} />
        ) : (
          <div id="editorJS" style={{ maxWidth: "100%" }} />
        )}
      </EditorWrapper>

      <Modal
        title="게시글 비밀번호 설정"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="설정"
        cancelText="취소"
        centered
      >
        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <p style={{ marginBottom: 12, fontSize: '13px', color: '#666' }}>
            비밀번호(숫자 4자리)를 입력해주세요.
          </p>
          <Input.Password
            placeholder="숫자 4자리"
            maxLength={4}
            value={password}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
              setPassword(onlyNumbers);
            }}
            onPressEnter={handleOk}
          />
        </div>
      </Modal>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  background-color: #fff;
  min-width: 70%;
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;
`;

const PostTitle = styled.div`
  margin: 0 0 12px;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.3;
  color: #262626;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const AuthorsWrapper = styled.div`
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const AuthorsLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8c8c8c;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AuthorsList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row-reverse;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const EditorWrapper = styled.div`
  overflow: hidden;
  word-break: break-word;
`;

export default PostContentSection;